// @flow strict
import type {TimeOff, BookingSlot} from 'src/types/scheduler/settings.js';
import type {MenuOption} from '@spaced-out/ui-design-system/lib/components/Menu/Menu';
// $FlowFixMe[nonstrict-import]
import {wrangleMoment, formatIsoDate} from 'src/utils/date-time-2';
// $FlowFixMe[nonstrict-import]
import {format, parseISO, isValid} from 'date-fns';
import {localeFormats} from 'src/constants/language-support';


type TranslationFunction = (key: string, value: string) => string;

export const formatDateTime = (
  isoDateString: string,
  locale: string = 'en',
): string => {
  const parsedDate = parseISO(isoDateString);
  if (!isValid(parsedDate)) {
    return '';
  }

  const {formatStr, locale: dateFnsLocale} =
    localeFormats[locale] || localeFormats.en;
  return format(parsedDate, formatStr, {locale: dateFnsLocale});
};

export const getSortedTimeOffs = (timeOffs: TimeOff[]): TimeOff[] => {
  if (!Array.isArray(timeOffs)) {
    return [];
  }

  return [...timeOffs].sort((left, right) => {
    const leftDate = wrangleMoment(left.startDatetime);
    const rightDate = wrangleMoment(right.startDatetime);
    return leftDate.getTime() - rightDate.getTime();
  });
};

export const getTimeOffActionOptions = (
  t: TranslationFunction,
): MenuOption[] => [
  {key: 'edit', label: `${t('Edit', 'Edit')}`},
  {
    key: 'delete',
    label: `${t('Delete', 'Delete')}`,
  },
];

// get the next rule id for a new booking hour
export const getNextRuleId = (bookingHours: BookingSlot[]): string => {
  if (bookingHours.length === 0) {
    return '0';
  }
  const sortedBookingHours = bookingHours
    .map(({ruleId}) => Number(ruleId))
    .sort((a, b) => b - a);
  const highestRuleId = sortedBookingHours[0];
  return String(highestRuleId + 1);
};

// get the min date with time for the time off date picker
export const getMinDateWithTime = (): string => {
  const minDate = formatIsoDate(new Date());
  const minDateWithTime = `${minDate}T00:00:00`;
  return minDateWithTime;
};

export const getLanguageLabelFromKey = (
  key: string,
  label?: string,
): string => {
  const languageMap = {
    en: 'English',
    es: 'Español',
    fr: 'Français',
    it: 'Italiano',
    pt: 'Português',
    ja: '日本語',
    af: 'Afrikaans',
    ar: 'العربية',
    hy: 'Հայերեն',
    az: 'Azərbaycan dili',
    eu: 'Euskara',
    be: 'Беларуская',
    bn: 'বাংলা',
    bs: 'Bosanski',
    bg: 'Български',
    ca: 'Català',
    zh: '中文',
    hr: 'Hrvatski',
    cs: 'Čeština',
    da: 'Dansk',
    nl: 'Nederlands',
    et: 'Eesti',
    fi: 'Suomi',
    ka: 'ქართული',
    de: 'Deutsch',
    el: 'Ελληνικά',
    gu: 'ગુજરાતી',
    he: 'עברית',
    hu: 'Magyar',
    is: 'Íslenska',
    id: 'Bahasa Indonesia',
    kk: 'Қазақ тілі',
    ko: '한국어',
    lv: 'Latviešu',
    lt: 'Lietuvių',
    mk: 'Македонски',
    ms: 'Bahasa Melayu',
    mr: 'मराठी',
    mn: 'Монгол',
    pl: 'Polski',
    pa: 'ਪੰਜਾਬੀ',
    ro: 'Română',
    ru: 'Русский',
    sr: 'Српски',
    sk: 'Slovenčina',
    so: 'Soomaali',
    sv: 'Svenska',
    ta: 'தமிழ்',
    te: 'తెలుగు',
    th: 'ไทย',
    tr: 'Türkçe',
    uk: 'Українська',
    vi: 'Tiếng Việt',
    cy: 'Cymraeg',
    zu: 'isiZulu',
    'fr-CA': 'Français canadien',
  };

  return languageMap[key] || label || 'English';
};
